import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { useGetApiCompaniesCurrentEmployeesReportingPresencequotaYearMonth } from '@/api/generated/company-management-user/company-management-user'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Rectangle, Legend } from 'recharts'
import { CHART_COLORS, YEAR_OPTIONS, getMonthLabels, CURRENT_YEAR, CURRENT_MONTH } from './constants'
import { ChartCard } from '../components/ChartCard'
import { FilterRowCharts } from '@/components/common/filters/FilterRowCharts'
import { FilterRowChartItem } from '@/components/common/filters/FilterRowChartItem'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

export function PresenceQuotaChart() {
  const { t } = useTranslation(['dashboard', 'common'])
  const [year, setYear] = useState(CURRENT_YEAR)
  const [month, setMonth] = useState(CURRENT_MONTH)
  const monthLabels = getMonthLabels(t)

  const { data, isFetching, isError } = useGetApiCompaniesCurrentEmployeesReportingPresencequotaYearMonth(year, month)

  const chartData = (data?.data ?? [])
    .map((r, i) => ({
      name: r.actionModuleName || t('charts.presenceQuota.moduleFallback', { id: r.actionModuleId }),
      value: parseFloat(r.averagePresenceQuota.toFixed(2)),
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }))

  function chart(){
    return (
      <LoaderGeneral isLoading={isFetching} isError={isError} isEmpty={chartData.length === 0} className="h-[260px]">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 48, left: 8 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--foreground)' }} tickLine={false} axisLine={false} angle={-35} textAnchor="end" interval={0} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="rounded-lg border bg-card px-3 py-2 shadow-md text-sm">
                    <p className="font-semibold text-card-foreground mb-0.5">{payload[0].payload.name}</p>
                    <p className="text-primary">{t('charts.presenceQuota.tooltipLabel')}: <span className="font-bold">{payload[0].value?.toString()}</span></p>
                  </div>
                ) : null
              }
              cursor={{ fill: 'var(--muted)' }}
            />
            <Legend formatter={() => t('charts.presenceQuota.legend')} />
            <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
              shape={(props: any) => <Rectangle {...props} fill={props.payload.fill} />}
            />
          </BarChart>
        </ResponsiveContainer>
      </LoaderGeneral>
    )
  }

  function controls() {
    return (
      <FilterRowCharts>
        <FilterRowChartItem label={t('charts.filters.year')}>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEAR_OPTIONS.map((year) => <SelectItem key={year} value={String(year)}>{year}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterRowChartItem>
        <FilterRowChartItem label={t('charts.filters.month')}>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthLabels.map((label, i) => <SelectItem key={i + 1} value={String(i + 1)}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterRowChartItem>
      </FilterRowCharts>
    )
  }

  return (
    <ChartCard
      title={t('charts.presenceQuota.title')}
      chart={chart()}
      controls={controls()} 
    />
  )
}
